/**
 * Client for the Worker API.
 *
 * Every request carries a Firebase token. The server is the sole authority on
 * results: the browser never receives answer keys before an attempt is
 * finished, so there is no client-side grading here at all.
 */
import { firebaseConfigured, idToken } from "./firebase";
import { endpointOf, track } from "./analytics";
import type {
  PublicElementsTask,
  PublicListeningTask,
  PublicReadingTask,
  SetSummary,
} from "@shared/public";
import type { WritingTask } from "@shared/schema";
import type { Key } from "@shared/items";
import type { SpeakingSheet } from "@shared/speaking";
import type { OralPartId, OralPool, OralReview, OralTurn } from "@shared/oral";
import type { OralStats } from "@shared/oral-stats";
import type { AccountCounts, ExamCounts } from "@shared/funnel";
import type { Addressed } from "@shared/oral-address";
import type { Level, ModuleId } from "@shared/structure";
import type { KindTally } from "@shared/stats";
import type { OralPack, Plan } from "@shared/plans";
import type { WritingReviewSet } from "@shared/writing-review";
import type { TimeStat } from "@shared/attempt-times";
import type { SignupSource } from "@shared/signup-source";
import type { Entry } from "@shared/entry";
import type { AnnouncementInput, LiveAnnouncement } from "@shared/announcement";
import type { AfterEventKind, AttemptEventKind, BlockSummary } from "@shared/attempt-log";
import type { ExamGoal, ExamWhen, OnboardingStep, StartTime } from "@shared/onboarding";
import type { PaywallPlacement } from "@shared/paywall";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
  }
}

/**
 * With no Firebase configured we work locally under a fixed identity. The
 * Worker accepts this header only when it too has no Firebase project — that
 * is, never in a deployment.
 */
async function authHeaders(): Promise<Record<string, string>> {
  if (!firebaseConfigured) return { "x-dev-uid": "local-dev" };
  lastToken = await idToken();
  return { authorization: `Bearer ${lastToken}` };
}

/**
 * The token of the last call, for a request that has to leave synchronously —
 * a page going away does not wait for an await. An exam page calls the API
 * every few seconds, so this is minutes old at most.
 */
let lastToken: string | null = null;

/**
 * Fire and forget, surviving the page that sends it (`keepalive`). For the
 * listening log, which is written as the tab is closed: no answer is read, no
 * error is shown, and a failure is not an `api_error` — losing one moment of
 * the log costs the candidate nothing.
 */
function beacon(path: string, body: unknown): void {
  if (firebaseConfigured && !lastToken) return;
  const auth: Record<string, string> = firebaseConfigured
    ? { authorization: `Bearer ${lastToken}` }
    : { "x-dev-uid": "local-dev" };
  try {
    void fetch(`/api${path}`, {
      method: "POST",
      keepalive: true,
      headers: { "content-type": "application/json", ...auth },
      body: JSON.stringify(body),
    }).catch(() => undefined);
  } catch {
    // A browser that refuses keepalive here has nothing better to offer.
  }
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      ...init,
      headers: {
        ...(init.body ? { "content-type": "application/json" } : {}),
        ...(await authHeaders()),
        ...init.headers,
      },
    });
  } catch {
    // The browser could not reach the server at all: offline, or the API is
    // not running. "Failed to fetch" tells nobody anything.
    track("api_error", { endpoint: endpointOf(path), status: 0, error_code: "network" });
    throw new ApiError(
      "Nie udało się połączyć z serwerem. Sprawdź połączenie z internetem.",
      0,
      "network",
    );
  }

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; code?: string };
    // An empty body on a 5xx means nothing answered on the other side —
    // in development that is almost always a Worker that was never started.
    // Say so only in development: in production the hint is noise to the reader
    // and the only person who could act on it is not there.
    const fallback =
      res.status >= 500
        ? import.meta.env.DEV
          ? "Serwer nie odpowiada. W trybie deweloperskim uruchom `npm run dev:api`."
          : "Serwer nie odpowiada. Spróbuj ponownie za chwilę."
        : `Błąd ${res.status}`;
    // A 409 that says the thing was already done is how the server answers the
    // second of two identical calls (a tab sent back to the background, a result
    // page opened twice): expected, so not a fault to count.
    if (!(res.status === 409 && (body.code === "module_closed" || body.code === "done"))) {
      track("api_error", {
        endpoint: endpointOf(path),
        status: res.status,
        error_code: body.code ?? "none",
      });
    }
    throw new ApiError(body.error ?? fallback, res.status, body.code);
  }
  return (await res.json()) as T;
}

const post = <T>(path: string, body?: unknown): Promise<T> =>
  call<T>(path, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) });

const put = <T>(path: string, body: unknown): Promise<T> =>
  call<T>(path, { method: "PUT", body: JSON.stringify(body) });

const del = <T>(path: string): Promise<T> => call<T>(path, { method: "DELETE" });

/* ------------------------------------------------------------------ */
/* Response types                                                      */
/* ------------------------------------------------------------------ */

export interface SetProgress {
  attempts: number;
  finished: number;
  bestTest: number | null;
  bestTestMax: number | null;
  lastStartedAt: number | null;
  /** Blocks of this set handed in at least once, in an exam or in practice. */
  triedModules: ModuleId[];
  /** The best marked oral session on this set; `null` before one. */
  oral: { points: number; max: number } | null;
}

export interface OralBalance {
  free: number;
  pass: number;
  grant: number;
  pack: number;
  left: number;
}

export interface OralSessionSummary {
  id: string;
  status: "active" | "marking" | "marked";
  createdAt: number;
  started: boolean;
  points: number | null;
  max: number;
  level: Level | null;
}

export interface OralSessionState {
  session: OralSessionSummary & {
    setId: string;
    addressed: Addressed;
    topic: number;
    partner: { name: string; gender: Addressed };
  };
  sheet: SpeakingSheet;
  turns: OralTurn[];
  review: OralReview | null;
}

export type SetInfo = SetSummary & { unlocked: boolean };
export type SetCard = SetInfo & { progress: SetProgress };

export interface AttemptScores {
  listening: number | null;
  reading: number | null;
  elements: number | null;
  test: number;
  testMax: number;
  /** `null` when the attempt did not cover all three test modules. */
  testScaled: number | null;
  coversAllTests: boolean;
  writing: number | null;
  /**
   * The level the test modules reach, or `null` after a partial attempt.
   *
   * It used to arrive inside a certificate verdict. There is no verdict any
   * more: telc grants a certificate on the oral exam plus one other module,
   * and the oral exam is the part with no recording to mark.
   */
  testLevel: Level | null;
}

export interface AttemptState {
  id: string;
  setId: string;
  /** Set metadata, needed to number items the way the S30 sheet does. */
  set: SetInfo | null;
  mode: "exam" | "practice";
  status: "active" | "finished" | "abandoned";
  currentModule: ModuleId | null;
  /** Computed by the server; the browser clock only counts down from it. */
  secondsLeft: number | null;
  startedAt: number;
  finishedAt: number | null;
  /**
   * How far into the listening recording the block is, in seconds, or `null`
   * until the candidate has started it. Until then the exam shows none of the
   * listening tasks — the block's clock starts with the recording, so a paper
   * read beforehand would be time the exam does not give.
   */
  recordingElapsed: number | null;
  submittedModules: ModuleId[];
  answers: Record<string, string>;
  writing: {
    choice: number | null;
    /** One entry per text the task asks for; index 1 is the state exam's second. */
    texts: string[];
    /** Written by the marking; the browser never fills these in. */
    marks: Record<string, number | boolean> | null;
    points: number | null;
    /** The markings themselves — one per text — once they have been made. */
    review: WritingReviewSet | null;
    /** A marking is already running — from another tab, or an earlier load. */
    reviewPending: boolean;
    /** False when the deployment has no OpenAI key: no button, and we say why. */
    canReview: boolean;
  };
  scores: AttemptScores | null;
}

export type ModuleTasks = PublicListeningTask[] | PublicReadingTask[] | PublicElementsTask[] | WritingTask[];

export interface AttemptSummary {
  id: string;
  setId: string;
  /** Position of the set, which is also its name: 3 means "Zestaw 3". */
  setNumber: number | null;
  mode: "exam" | "practice";
  status: AttemptState["status"];
  startedAt: number;
  finishedAt: number | null;
  scores: AttemptScores | null;
}

export type { KindTally } from "@shared/stats";

/** One text the candidate has written, as the writing history lists it. */
export interface WritingHistoryEntry {
  attemptId: string;
  setId: string;
  setNumber: number | null;
  startedAt: number;
  finishedAt: number | null;
  choice: number | null;
  words: number;
  excerpt: string;
  /** What the block was worth on that paper: 20 on a telc set, 30 at B1. */
  max: number;
  /** `null` when the text was never marked — not a zero. */
  points: number | null;
  marks: Record<string, number> | null;
  offTopic: boolean;
}

export interface ReviewData {
  attemptId: string;
  setId: string;
  /**
   * Not only strings: a grouped match, a written gap and an open answer carry
   * the shapes `Key` describes. Typing them as strings is how the review came
   * to hand an object to React and render nothing at all.
   */
  keys: Record<string, Key>;
  /** Why the key is the key; only for items the content explains. */
  explanations: Record<string, string>;
  /**
   * What the recording said, by module and task index. Present for a past paper,
   * whose transcript the publisher prints; a telc set carries its scripts in the
   * task content instead.
   */
  transcripts: Record<string, Record<string, string>>;
  answers: Record<string, { answer: string | null; correct: boolean | null; note?: string }>;
}

/* ------------------------------------------------------------------ */
/* Endpoints                                                           */
/* ------------------------------------------------------------------ */

export interface AccessState {
  /** A tutor's code for their students, when this account has one. */
  tutor: { code: string; percent: number } | null;
  /** `null` when no pass is running: everything but the free sets is closed. */
  pass: { expiresAt: number; since: number; daysLeft: number } | null;
  /** Every term on sale, shortest first. The buyer picks one at checkout. */
  plans: Plan[];
  /** A running sale on the passes, or null. */
  promo: { percent: number; endsAt: number } | null;
  /** Oral-exam session packs on sale. */
  packs: OralPack[];
  /** Oral-exam sessions left, pool by pool. */
  oral: OralBalance;
  /** How many sets the pass opens today. */
  setCount: number;
  /** False when the deployment has no Stripe keys — nothing to buy, and we say so. */
  canBuy: boolean;
}

export const api = {
  /**
   * `language` decides which language the welcome letter and later mailings are
   * in; `marketingConsent` is the box ticked on the sign-in page, sent only when
   * it was ticked; `source` is where the visit came from (`signup-source.ts`),
   * which the Worker writes only to an account this call creates; `entry` is
   * the page this tab entered on (`entry.ts`), counted — never written to the
   * account — when this call creates one.
   */
  session: (
    language: string,
    marketingConsent: boolean,
    source: SignupSource | null,
    entry: Entry | null,
    freshEntry = false,
  ) =>
    post<{ uid: string; email: string | null; admin?: boolean; onboard?: boolean }>("/session", {
      language,
      ...(marketingConsent ? { marketingConsent: true } : {}),
      ...(source ? { source } : {}),
      ...(entry ? { entry } : {}),
      ...(entry && freshEntry ? { fresh: true } : {}),
    }),

  sets: () =>
    call<{
      sets: SetCard[];
      activeAttempt: {
        id: string;
        set_id: string;
        mode: string;
        current_module: string | null;
        /** A block's clock is still going — the exam is being sat right now. */
        running: boolean;
      } | null;
    }>("/sets"),

  /**
   * A short-lived ticket for streaming a set's recording.
   *
   * An `<audio>` element cannot send an Authorization header, so the src has to
   * carry proof of its own. This is that proof — signed over one user and one
   * set, good for minutes, and useless anywhere else.
   */
  /**
   * Marks the answers nothing can mark by comparing — the state exam's sentence
   * transformations. Safe to call twice: the second call is told it is done.
   */
  openReview: (attemptId: string, lang: string) =>
    post<{ points: number; max: number }>(`/attempts/${attemptId}/open-review?lang=${lang}`),

  audioTicket: (setId: string) =>
    post<{ ticket: string; expiresAt: number }>(`/sets/${setId}/audio-ticket`),

  moduleTasks: (setId: string, moduleId: ModuleId) =>
    call<{ setId: string; moduleId: ModuleId; tasks: ModuleTasks }>(
      `/sets/${setId}/modules/${moduleId}`,
    ),

  /** One set's oral-exam sheet. telc sets only — see `shared/speaking.ts`. */
  speakingSheet: (setId: string) =>
    call<{ setId: string; sheet: SpeakingSheet }>(`/sets/${setId}/speaking`),

  oralSessions: (setId: string) =>
    call<{
      configured: boolean;
      /** The account's form of address; `null` until chosen. */
      addressed: Addressed | null;
      balance: OralBalance;
      sessions: OralSessionSummary[];
    }>(`/sets/${setId}/oral`),

  oralAddress: () => call<{ addressed: Addressed | null }>("/account/address"),

  setOralAddress: (addressed: Addressed) =>
    put<{ addressed: Addressed }>("/account/address", { addressed }),

  /** `addressed` only when the candidate has just chosen it; the account's otherwise. */
  startOral: (setId: string, addressed: Addressed | null, topic: number) =>
    post<{ sessionId: string }>(`/sets/${setId}/oral`, { ...(addressed ? { addressed } : {}), topic }),

  oralSession: (id: string) => call<OralSessionState>(`/oral/${id}`),

  /** One examiner line: its words, and the speech as a data URL. */
  oralSay: (id: string, part: OralPartId | "end", index = 0) =>
    post<{ text: string; audio: string }>(`/oral/${id}/say`, { part, index }),

  /** One recorded answer. The body is the recording; the server transcribes it. */
  oralAnswer: (id: string, part: OralPartId, index: number, audio: Blob, last = false, wrap = false) =>
    call<{
      turn: OralTurn;
      /** The first answer came back empty: record it again, the session is not spent. */
      silent?: boolean;
      followUps?: string[];
      partner?: { text: string; audio: string };
    }>(`/oral/${id}/answer?part=${part}&index=${index}${last ? "&last=1" : ""}${wrap ? "&wrap=1" : ""}`, {
      method: "POST",
      body: audio,
      headers: { "content-type": audio.type || "application/octet-stream" },
    }),

  finishOral: (id: string, lang: string) =>
    post<{ review: OralReview }>(`/oral/${id}/finish`, { lang }),

  /** Usual minutes of a full exam and of a grammar practice run; `null` without enough runs. */
  typicalTimes: () => call<{ exam: number | null; practice: number | null; oral: number | null }>("/typical-times"),
  /** The first-visit wizard, a step at a time — `{}` when it comes on screen; see `shared/onboarding.ts`. */
  onboarding: (body: OnboardingStep) => put<{ ok: true }>("/account/onboarding", body),

  /** First press on a door to the pass, or `/pass` on screen — the admin funnel's two steps. */
  paywallStep: (step: "click" | "seen", placement?: PaywallPlacement) =>
    put<{ ok: true }>("/account/paywall", { step, ...(placement ? { placement } : {}) }),

  startAttempt: (setId: string, mode: "exam" | "practice", moduleId?: ModuleId) =>
    post<AttemptState>("/attempts", { setId, mode, moduleId }),

  attempt: (id: string) => call<AttemptState>(`/attempts/${id}`),

  /** Attempts, newest first. Scoped to one set when `setId` is given. */
  attempts: (setId?: string) =>
    call<{ attempts: AttemptSummary[] }>(
      setId ? `/attempts?setId=${encodeURIComponent(setId)}` : "/attempts",
    ),

  /** Graded answers so far, grouped by kind of task. Carries no keys. */
  stats: () => call<{ tallies: KindTally[] }>("/stats"),

  /** Whether a pass is running, and what is on sale if not. */
  access: () => call<AccessState>("/access"),

  /** This account's orders, newest first — how a checkout's return learns it was paid. */
  /** The buyer is back from Stripe's cancel link; the first time is kept. */
  orderReturned: (id: string) => put<{ ok: true }>(`/orders/${id}/returned`, {}),

  orders: () => call<{ orders: { id: string; status: string; plan_id: string }[] }>("/orders"),

  /** Opens a Stripe Checkout session; the answer is where to send the browser. */
  checkout: (planId: string, language: string) =>
    post<{ orderId: string; url: string }>("/checkout", { planId, language }),

  /** Everything stored under this account, as one JSON document. */
  exportAccount: () => call<Record<string, unknown>>("/account/export"),

  /** Deletes the account server-side. The Firebase user goes separately. */
  deleteAccount: () => del<{ ok: true }>("/account"),
  mailing: () => call<{ marketing: boolean; ask: boolean }>("/account/mailing"),
  /**
   * `via: "prompt"` when it answers the one-time question on the list of sets.
   */
  setMailing: (marketing: boolean, via: "account" | "prompt" = "account") =>
    put<{ marketing: boolean }>("/account/mailing", { marketing, via }),

  saveAnswers: (id: string, moduleId: ModuleId, answers: Record<string, string>) =>
    put<{ ok: true; saved: number }>(`/attempts/${id}/answers`, { moduleId, answers }),

  saveWriting: (id: string, body: { choice?: number; texts?: string[] }) =>
    put<{ ok: true }>(`/attempts/${id}/writing`, body),

  submitModule: (id: string, moduleId: ModuleId) =>
    post<{
      ok: true;
      nextModule: ModuleId | null;
      secondsLeft: number | null;
      score: number | null;
      max: number | null;
    }>(`/attempts/${id}/modules/${moduleId}/submit`),

  /** Stops an attempt without scoring it: the clock stops, nothing is graded. */
  abandon: (id: string) => post<AttemptState>(`/attempts/${id}/abandon`),
  /** Lets the listening recording start, and starts the block's clock with it. */
  startRecording: (id: string) => post<AttemptState>(`/attempts/${id}/start-recording`),
  /** The exam's track was moved to `to`, the end of a silence; the server keeps how far ahead it is. */
  skipPause: (id: string, to: number) =>
    post<AttemptState>(`/attempts/${id}/skip-pause`, { to }),
  /** One moment of an attempt; see `shared/attempt-log.ts`. Never throws, never waits. */
  attemptEvent: (
    id: string,
    event:
      | { kind: AttemptEventKind; module: ModuleId; pos: number | null; answered: number | null }
      | { kind: AfterEventKind },
  ) => beacon(`/attempts/${id}/events`, event),

  finish: (id: string) => post<AttemptState>(`/attempts/${id}/finish`),

  /**
   * Marks the writing of a finished attempt. Once per attempt: asking twice
   * returns the marking that already exists.
   */
  reviewWriting: (id: string, lang: string) =>
    post<{ review: WritingReviewSet }>(`/attempts/${id}/writing-review`, { lang }),

  review: (id: string) => call<ReviewData>(`/attempts/${id}/review`),

  /** Every text this account has written, newest first. */
  writingHistory: () => call<{ texts: WritingHistoryEntry[] }>("/writing"),

  /* The operator's view — answers 404 to anyone the Worker does not recognise. */
  adminOverview: () => call<AdminOverview>("/admin/overview"),
  adminUsers: () => call<{ users: AdminUserRow[] }>("/admin/users"),
  adminUser: (uid: string) => call<AdminUserDetail>(`/admin/users/${encodeURIComponent(uid)}`),
  adminTutor: (uid: string, profile: string, access: boolean) =>
    post<{ ok: true; code: string; already?: boolean }>(
      `/admin/users/${encodeURIComponent(uid)}/tutor`,
      { profile, access },
    ),
  adminGrant: (uid: string, days: number, note: string) =>
    post<{ ok: true; startsAt: number; expiresAt: number }>(
      `/admin/users/${encodeURIComponent(uid)}/grant`,
      { days, note },
    ),
  adminMailingBackfill: () => post<{ added: number }>("/admin/mailing/backfill"),
  adminNotices: () => call<{ configured: boolean; notices: AdminNotice[] }>("/admin/notices"),
  /** The notice in all three languages to the admin's own address. */
  adminTestNotice: (id: string) =>
    post<{ to: string }>(`/admin/notices/${encodeURIComponent(id)}/test`),
  /** One batch; the page asks again while anything is waiting. */
  adminSendNotice: (id: string) =>
    post<{ sent: number; failed: boolean; waiting: number }>(
      `/admin/notices/${encodeURIComponent(id)}/send`,
    ),
  adminOral: () => call<AdminOralStats>("/admin/oral"),
  adminFunnel: (days: number) => call<AdminFunnel>(`/admin/funnel?days=${days}`),
  adminEntries: (days: number) => call<AdminEntries>(`/admin/entries?days=${days}`),
  adminExits: (days: number) => call<AdminExits>(`/admin/exits?days=${days}`),
  adminOnboarding: (days: number) => call<AdminOnboarding>(`/admin/onboarding?days=${days}`),
  adminOralGrant: (uid: string, sessions: number, note: string) =>
    post<{ ok: true; balance: OralBalance }>(`/admin/users/${encodeURIComponent(uid)}/oral-grant`, {
      sessions,
      note,
    }),
  adminOralTakeBack: (grantId: string) =>
    post<{ ok: true; takenBack: number }>(`/admin/oral-grants/${encodeURIComponent(grantId)}/take-back`),
  adminEndGrant: (passId: string) =>
    post<{ ok: true }>(`/admin/passes/${encodeURIComponent(passId)}/end`),
  adminAnnouncements: () => call<{ announcements: AdminAnnouncement[] }>("/admin/announcements"),
  adminCreateAnnouncement: (a: AnnouncementInput) => post<{ id: string }>("/admin/announcements", a),
  adminUpdateAnnouncement: (id: string, a: AnnouncementInput) =>
    put<{ ok: true }>(`/admin/announcements/${encodeURIComponent(id)}`, a),
  adminEndAnnouncement: (id: string) =>
    post<{ ok: true }>(`/admin/announcements/${encodeURIComponent(id)}/end`),
  adminDeleteAnnouncement: (id: string) =>
    del<{ ok: true }>(`/admin/announcements/${encodeURIComponent(id)}`),

  /** The line of news under the header, in the reader's language, or `null`. */
  announcement: (lang: string) =>
    call<{ announcement: LiveAnnouncement | null }>(`/announcements?lang=${encodeURIComponent(lang)}`),
  dismissAnnouncement: (id: string) =>
    post<{ ok: true }>(`/announcements/${encodeURIComponent(id)}/dismiss`),
};

/* ------------------------------------------------------------------ */
/* Admin                                                               */
/* ------------------------------------------------------------------ */

export interface AdminOrder {
  id: string;
  uid?: string;
  email?: string | null;
  status: "pending" | "paid" | "cancelled" | "refunded";
  planId: string;
  amountMinor: number;
  currency: string;
  createdAt: number;
  paidAt: number | null;
  /** 1 once Stripe's checkout was opened for it; 0 when the session never opened. */
  opened: number;
  /** Came back through Stripe's cancel link. */
  returnedAt: number | null;
  /** Stripe closed the session unpaid. */
  expiredAt: number | null;
  /** When a paid set was first used after this payment; `null` if not, or if the use began before it. */
  firstUseAfterPaid: number | null;
  /** 1 when a paid set was used at any point after this payment. */
  usedAfterPaid: number;
}

/** A service notice from `worker/mailing/notices.ts`, with how far its sending got. */
export interface AdminNotice {
  id: string;
  subject: string;
  /** Accounts created before this get it. */
  before: number;
  sent: number;
  waiting: number;
}

export interface AdminAnnouncement {
  id: string;
  pl: string;
  uk: string;
  /** Null on an announcement written before Russian was added. */
  ru: string | null;
  en: string;
  link: string | null;
  labelPl: string | null;
  labelUk: string | null;
  labelRu: string | null;
  labelEn: string | null;
  startsAt: number;
  endsAt: number;
  createdBy: string;
  /** Accounts, the admins' left out, that closed it or followed its link. */
  dismissed: number;
}

/** `GET /api/admin/funnel` — counts; the steps and percentages are `shared/funnel.ts`. */
export interface EntryRow {
  key: string;
  visits: number;
  signups: number;
  /** Of `visits`, by somebody who already had an account. */
  returning: number;
}

/** `/admin/entries`: entries and sign-ups by page and by source, everyone counted. */
export interface AdminEntries {
  days: number;
  /** The first day anything was counted (UTC, `YYYY-MM-DD`), or null. */
  since: string | null;
  pages: EntryRow[];
  sources: EntryRow[];
}

export interface AdminFunnel {
  days: number;
  /**
   * The app's doors to the pass (`shared/paywall.ts`): presses and the people
   * who pressed, then orders opened within the hour after a press and paid.
   * `placement` null holds orders that came from no press.
   */
  doors: { placement: string | null; clicks: number; users: number; orders: number; paid: number }[];
  accounts: AccountCounts;
  exams: Record<"all" | "telc" | "certyfikat", ExamCounts & { running: number }>;
}

/** `GET /api/admin/onboarding` — the first-visit wizard, from the database. */
export interface AdminOnboarding {
  days: number;
  /** Accounts not yet asked: they have not opened `/app` since it shipped. */
  waiting: number;
  /** Shown the wizard, then answered step 1, 2 and 3 (the last completes it). */
  steps: { seen: number; goal: number; when: number; time: number };
  short: { chose: number; started: number; handedIn: number };
  full: { chose: number; started: number; finished: number };
  /** Started: an answer with words in it, which is when a session counts. */
  oral: { chose: number; started: number; marked: number };
  choices: { question: "goal" | "when" | "time"; answer: string; count: number }[];
}

/**
 * `GET /api/admin/exits` — blocks of the exam and of practice that were opened
 * and never handed in, newest first; and, for closed attempts, what was done on
 * the result and review screens.
 */
export interface AdminExits {
  days: number;
  exits: (BlockSummary & {
    startedAt: number;
    mode: "exam" | "practice";
    module: ModuleId;
    family: string;
    setTitle: string | null;
    /** Items in the block; null for Writing, whose `answered` is words. */
    total: number | null;
  })[];
  after: { mode: "exam" | "practice"; finished: number; did: Partial<Record<AfterEventKind, number>> }[];
}

export interface AdminOverview {
  generatedAt: number;
  users: {
    total: number;
    new7: number;
    new30: number;
    active7: number;
    active30: number;
    withAttempts: number;
    inMailingList: number;
    notInMailingList: number;
    marketingConsent: number;
  };
  orders: {
    buyers: number;
    clickedPay: number;
    clickedNotPaid: number;
    revenueMinor: number;
    paid: number;
    pending: number;
    refunded: number;
    cancelled: number;
    /** The same, for the pass alone and for oral packs alone. */
    passClickedPay: number;
    passBuyers: number;
    passClickedNotPaid: number;
    passRevenueMinor: number;
    passPending: number;
    oralClickedPay: number;
    oralBuyers: number;
    oralClickedNotPaid: number;
    oralRevenueMinor: number;
    oralPending: number;
  };
  variants: {
    variant: string;
    users: number;
    attempts: number;
    finished: number;
    exams: number;
    examsFinished: number;
    practice: number;
    avgExamPct: number | null;
  }[];
  modules: { family: string; mode: string; module: ModuleId; submitted: number }[];
  daily: {
    day: string;
    signups: number;
    telc: number;
    certyfikat: number;
    unknown: number;
    users: number;
  }[];
  sets: {
    key: string | null;
    title: string;
    variant: string;
    source: string;
    free: number;
    attempts: number;
    users: number;
    finished: number;
  }[];
  recentOrders: AdminOrder[];
  times: TimeStat[];
}

/** Where an account came from; all null for one from before, or without consent. */
export interface AdminSource {
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  utmContent: string | null;
  utmTerm: string | null;
  referrerHost: string | null;
  landingPath: string | null;
  sourceAt: number | null;
}

export interface AdminUserRow extends AdminSource {
  uid: string;
  email: string | null;
  name: string | null;
  createdAt: number;
  lastSeenAt: number;
  /** Consent to marketing mailings as it stands; `marketingAt`/`Via` are its last change. */
  marketing: boolean;
  marketingAt: number | null;
  marketingVia: "sign_in" | "account" | "prompt" | null;
  /** The first-visit answers (`shared/onboarding.ts`); `onboardedAt` once all three are in. */
  examGoal: ExamGoal | null;
  examWhen: ExamWhen | null;
  startTime: StartTime | null;
  onboardedAt: number | null;
  /** First press on a door to the pass, and first time `/pass` was on screen. */
  paywallClickedAt: number | null;
  passSeenAt: number | null;
  attempts: number;
  finished: number;
  exams: number;
  examsFinished: number;
  practice: number;
  practiceFinished: number;
  telc: number;
  certyfikat: number;
  lastAttemptAt: number | null;
  ordersPaid: number;
  ordersRefunded: number;
  ordersUnpaid: number;
  paidMinor: number;
  lastOrderAt: number | null;
  passUntil: number | null;
  /** Oral sessions with a word said, and of those marked. */
  oralStarted: number;
  oralMarked: number;
}

export type AdminOralStats = OralStats & { generatedAt: number };

export interface AdminUserDetail {
  tutor: { code: string; profileUrl: string; createdAt: number; students: number } | null;
  user: AdminSource & {
    uid: string;
    email: string | null;
    name: string | null;
    createdAt: number;
    lastSeenAt: number;
    stripeCustomer: string | null;
  };
  attempts: {
    id: string;
    setKey: string | null;
    setTitle: string | null;
    variant: string;
    mode: "exam" | "practice";
    status: "active" | "finished" | "abandoned";
    currentModule: ModuleId | null;
    startedAt: number;
    finishedAt: number | null;
    test: number | null;
    testMax: number | null;
    writing: number | null;
    modules: string | null;
  }[];
  orders: AdminOrder[];
  passes: { id: string; source: string; startsAt: number; expiresAt: number; note: string | null }[];
  paidOpens: {
    setId: string;
    setTitle: string | null;
    variant: string;
    action: "content" | "speaking" | "audio" | "attempt" | "oral";
    firstAt: number;
    lastAt: number;
    count: number;
  }[];
  oral: {
    balance: OralBalance;
    sessions: {
      id: string;
      setTitle: string | null;
      status: "active" | "marking" | "marked";
      pool: OralPool | null;
      createdAt: number;
      startedAt: number | null;
      finishedAt: number | null;
      points: number | null;
      level: string | null;
      sttSeconds: number;
      ttsChars: number;
      /** Parts the candidate answered in, comma-separated. */
      parts: string | null;
    }[];
    grants: { id: string; sessions: number; takenBack: number; note: string; createdAt: number }[];
  };
}
